import { useState } from 'react'
import {
  Base,
  CheckIcon,
  DuplicateIcon,
  ReverLoading
} from '@itsrever/design-system'
import { clsx } from 'clsx'
import { Trans, useTranslation } from 'react-i18next'
import { ApiKeyRow, accessLabel, createdLabel } from './types'

const {
  Button,
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogTitle,
  DialogDescription,
  DialogCloseButton,
  DialogHeader,
  DialogFooter,
  Card
} = Base

interface KeyValueDialogProps {
  isOpen: boolean
  apiKey?: ApiKeyRow
  // The plaintext, held only while this dialog is open and never put in the query cache.
  value?: string
  loading: boolean
  // True right after issuing, when the dialog is a hand-over rather than a lookup.
  justCreated: boolean
  // True when creating returned a key the group already held instead of issuing a new one.
  existing: boolean
  // False for REVER staff: this is their only sight of the value, so the copy must not promise
  // a second one.
  canReadAgain: boolean
  onClose: () => void
}

export const KeyValueDialog: React.FC<KeyValueDialogProps> = ({
  isOpen,
  apiKey,
  value,
  loading,
  justCreated,
  existing,
  canReadAgain,
  onClose
}) => {
  const { t, i18n } = useTranslation()
  const [copied, setCopied] = useState(false)

  if (!apiKey) return null

  // The write rejects outside a secure context. Confirm only what actually happened, and do not
  // let the rejection surface as an unhandled one.
  const copy = () => {
    if (!value) return
    navigator.clipboard
      ?.writeText(value)
      .then(() => setCopied(true))
      .catch(() => undefined)
  }

  const description = existing
    ? t('api_keys.value_dialog.existing_description')
    : justCreated
      ? canReadAgain
        ? t('api_keys.value_dialog.created_description_can_read')
        : t('api_keys.value_dialog.created_description_once')
      : t('api_keys.value_dialog.lookup_description', { group: apiKey.group })

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setCopied(false)
          onClose()
        }
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        <DialogPopup>
          <div className="flex w-[30rem] max-w-full flex-col gap-4">
            <DialogCloseButton />
            <DialogHeader>
              <DialogTitle>
                {justCreated && !existing ? (
                  <span className="flex items-center gap-2">
                    <CheckIcon className="size-5 text-green-600" />
                    {t('api_keys.value_dialog.created_title')}
                  </span>
                ) : (
                  apiKey.name
                )}
              </DialogTitle>
              <DialogDescription>{description}</DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-1.5">
              <span className="text-sm">
                {t('api_keys.value_dialog.key_label')}
              </span>
              <div
                className={clsx(
                  'flex items-center gap-2 rounded-lg border p-2.5',
                  justCreated && !existing
                    ? 'border-green-200 bg-green-50'
                    : 'border-neutral-200 bg-neutral-50'
                )}
              >
                {loading ? (
                  <ReverLoading />
                ) : (
                  <>
                    <code className="grow break-all font-mono text-sm text-neutral-900">
                      {value}
                    </code>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={copy}
                      aria-label={t('api_keys.value_dialog.copy_label')}
                    >
                      <span className="flex items-center gap-1.5">
                        {copied ? (
                          <CheckIcon className="size-4 text-green-600" />
                        ) : (
                          <DuplicateIcon className="size-4" />
                        )}
                        {copied
                          ? t('api_keys.value_dialog.copied')
                          : t('api_keys.value_dialog.copy')}
                      </span>
                    </Button>
                  </>
                )}
              </div>
              <p className="text-xs text-neutral-600">
                <Trans
                  i18nKey="api_keys.value_dialog.header_hint"
                  components={{ c: <code /> }}
                />
              </p>
            </div>

            <Card variant="flat" className="p-3">
              <dl className="flex flex-col gap-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-neutral-600">
                    {t('api_keys.value_dialog.group_label')}
                  </dt>
                  <dd className="text-right">{apiKey.group}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-neutral-600">
                    {t('api_keys.value_dialog.access_label')}
                  </dt>
                  <dd className="text-right">
                    {accessLabel(apiKey.scopes, t)}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-neutral-600">
                    {t('api_keys.value_dialog.created_label')}
                  </dt>
                  <dd className="text-right">
                    {createdLabel(apiKey, t, i18n.language)}
                  </dd>
                </div>
              </dl>
            </Card>

            <DialogFooter>
              <Button type="button" variant="primary" onClick={onClose}>
                {t('api_keys.value_dialog.done')}
              </Button>
            </DialogFooter>
          </div>
        </DialogPopup>
      </DialogPortal>
    </DialogRoot>
  )
}
